import { addDays } from 'date-fns/addDays'
import { parseISO } from 'date-fns/parseISO'
import { format } from 'date-fns/format'
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays'
import config from 'isomorphic-config'
import { CrossSellContentBlock, Booking, Product } from './types'

function getBaseServiceOptions(
  operationName: string,
  apiMetadata?: ApiMetadata
) {
  const serviceOptions = {
    url: `${config.client['pcln-graph'].url}?gqlOp=${operationName}`,
    method: 'POST',
    timeout: config.client['pcln-graph'].timeout,
    headers: {
      'apollographql-client-name': apiMetadata?.appName ?? 'next-landing',
      'apollographql-client-version': apiMetadata?.appVersion ?? '0.0.0.0'
    }
  }
  return serviceOptions
}

export function buildContent(booking: Booking): CrossSellContentBlock {
  const startDate = parseISO(booking.travelStartDateTime)
  const endDate = parseISO(booking.travelEndDateTime)
  const formattedDate = `${format(startDate, 'EEE, MMM dd')} - ${format(
    endDate,
    'EEE, MMM dd'
  )}`

  const hotelsXSellUrl = (destination: string, start: Date, end: Date) =>
    `/relax/in/${destination}/from/${format(start, 'yyyyMMdd')}/to/${format(
      end,
      'yyyyMMdd'
    )}/rooms/1`

  switch (booking.productId) {
    case Product.Hotel: {
      const checkInDate = parseISO(booking.utcCheckInDateTime)

      return {
        product: 'hotel',
        xSellMessaging: 'Your hotel booking unlocks up to 40% off rental cars',
        xSellTarget: 'Rental Cars',
        xSellTargetId: Product.Car,
        date: formattedDate,
        destination: booking.destinationCity,
        offerToken: booking.offerToken,
        headline: booking.destinationCity,
        subHeadline: `Check-in on ${format(checkInDate, 'EEE, MMM dd')}`,
        xSellUrl: `/drive/search/r/${booking.destinationCity},${
          booking.destinationCountryCode || ''
        }/${booking.destinationCity},${
          booking.destinationCountryCode || ''
        }/${format(startDate, 'yyyyMMdd')}-12:00/${format(
          endDate,
          'yyyyMMdd'
        )}-12:00/list`
      }
    }
    case Product.Flight: {
      const destination =
        booking.destinationCity || booking.destinationLocationCode

      const isSameDay = differenceInCalendarDays(endDate, startDate) < 1
      return {
        product: 'flight',
        xSellMessaging:
          'Your flight booking unlocks VIP hotel deals of up to 40% off',
        xSellTarget: 'Hotels',
        xSellTargetId: Product.Hotel,
        offerToken: booking.offerToken,
        date: formattedDate,
        destination,
        headline: `${booking.originLocationCode} - ${booking.destinationLocationCode}`,
        subHeadline: `Departs ${format(startDate, "EEE, MMM dd 'at' h:mm a")}`,
        xSellUrl: hotelsXSellUrl(
          destination,
          startDate,
          isSameDay ? addDays(endDate, 1) : endDate
        )
      }
    }
    case Product.Car: {
      const checkInDate = parseISO(booking.utcCheckInDateTime)

      return {
        product: 'car',
        xSellMessaging:
          'Your rental car booking unlocks VIP hotel deals of up to 40% off',
        xSellTarget: 'Hotels',
        xSellTargetId: Product.Hotel,
        offerToken: booking.offerToken,
        date: formattedDate,
        destination: booking.destinationCity,
        headline: `${booking.destinationCity}`,
        subHeadline: `Pickup on ${format(checkInDate, 'EEE, MMM do')}`,
        xSellUrl: hotelsXSellUrl(booking.destinationCity, startDate, endDate)
      }
    }
    // no default
  }
}

type ApiMetadata = {
  appName: string
  appVersion: string
}

export function buildItineraryDetailsRequest(
  offerToken: string,
  tokenType: string,
  apiMetadata?: ApiMetadata
) {
  return {
    ...getBaseServiceOptions('itineraryDetails', apiMetadata),
    data: {
      query: `{
        itineraryDetails(offerToken: "${offerToken}", tokenType: "${tokenType}") {
          hotelXsellCarousel {
            checkIn
            checkOut
            cityId
            cityName
          }
          rcXsellRequestParams {
            pickupDateTime
            returnDateTime
            pickupLocation
            returnLocation
          }
        }
      }`
    }
  }
}
